/**
 * Builds a concise team + roster context string for the Coach Steve AI agent.
 * Uses live app data so the agent can answer lineup, position, and attribute questions.
 */

import type { Team, Sport } from '../../types/core';
import type { PlayerCard } from '../../types/playerCard';
import { supabase } from '../../lib/supabase';
import { getCalculatedStats } from '../../utils/statCalculations';
import { INJURY_STATUS_LABELS } from '../../constants/injuries';

interface PlayerRecord {
  id: string;
  first_name: string;
  last_name: string;
  number?: number;
  position?: string;
  height?: string;
  weight?: number;
  speed?: number;
  strength?: number;
  experience?: string;
  injury_status?: string;
  batting_hand?: string;
  batting_average?: number;
  on_base_percentage?: number;
}

function formatBattingStat(val: number | undefined): string {
  if (val == null) return '—';
  return (typeof val === 'number' ? val.toFixed(3) : String(val)).replace(/^0/, '.');
}

/**
 * Build a single roster line for one player (with optional card data).
 */
function formatPlayerLine(
  player: PlayerRecord,
  card?: PlayerCard | null
): string {
  const num = player.number ?? '—';
  const name = `${player.first_name} ${player.last_name}`.trim() || 'Unknown';
  const pos = player.position || '—';
  const hw = [player.height, player.weight].filter(Boolean).join('/') || '—';
  const speed = player.speed != null ? String(player.speed) : '—';
  const str = player.strength != null ? String(player.strength) : '—';
  const exp = player.experience || '—';
  const injuryCode = player.injury_status || 'H';
  const injuryLabel = INJURY_STATUS_LABELS[injuryCode as keyof typeof INJURY_STATUS_LABELS] ?? injuryCode;
  const injury = `${injuryCode} (${injuryLabel})`;

  let line = `[#${num}] ${name} | pos: ${pos} | H/W: ${hw} | speed ${speed} str ${str} | exp: ${exp} | injury: ${injury}`;

  if (card) {
    const eligible = (card.eligible_positions && card.eligible_positions.length)
      ? card.eligible_positions.join(', ')
      : '—';
    const off = card.offense_designation || '—';
    const def = card.defense_designation || '—';
    const ovr = card.ovr_rating != null ? String(card.ovr_rating) : '—';
    line += `\n  Eligible: ${eligible} | Off: ${off} Def: ${def} | OVR: ${ovr}`;
  }

  const batHand = (player as any).batting_hand;
  const ba = (player as any).batting_average;
  const obp = (player as any).on_base_percentage;
  if (batHand || ba != null || obp != null) {
    line += `\n  Bat: ${batHand || '—'} BA ${formatBattingStat(ba)} OBP ${formatBattingStat(obp)}`;
  }

  return line;
}

/**
 * Build full team context string for the agent system prompt.
 * Pass in the current team and either:
 * - cardsWithPlayers: array of PlayerCard (each with .player) from getPlayerCardsForTeam, or
 * - playersOnly: array of Player from a direct roster fetch when no cards exist.
 */
export function buildTeamContextForAgent(
  team: Team,
  options: {
    cardsWithPlayers?: (PlayerCard & { player?: PlayerRecord })[];
    playersOnly?: PlayerRecord[];
  }
): string {
  const { cardsWithPlayers = [], playersOnly = [] } = options;
  const hasCards = cardsWithPlayers.length > 0;
  const roster = hasCards
    ? cardsWithPlayers
        .filter((c) => c.player)
        .map((c) => ({ player: c.player!, card: c }))
    : playersOnly.map((p) => ({ player: p, card: null }));

  if (roster.length === 0) {
    return `Team: ${team.name} | Sport: ${team.sport} | Age: ${team.ageLevel ?? '—'}\nRoster: No players in the app yet. Suggest they add players in Roster or Player Cards.`;
  }

  const header = [
    `Team: ${team.name}`,
    `Sport: ${team.sport}`,
    team.ageLevel ? `Age: ${team.ageLevel}` : '',
    team.experienceLevel ? `Level: ${team.experienceLevel}` : '',
  ]
    .filter(Boolean)
    .join(' | ');

  const playerLines = roster.map(({ player, card }) =>
    formatPlayerLine(player as PlayerRecord, card ?? undefined)
  );

  return [
    '--- LIVE TEAM DATA (from the app; use this to answer questions about lineups, positions, stats, and player attributes) ---',
    'Injury codes: H = Healthy, Q = Questionable, D = Doubtful, O = Out. When counting "injured" players, count only Q, D, O — never H (Healthy).',
    header,
    `Roster (${roster.length} players):`,
    ...playerLines,
    '---',
  ].join('\n');
}

/**
 * Fetch game stats (sport_stats with game_id set) for the team and return a context string
 * so the agent can suggest lineup optimizations based on performance.
 */
export async function buildGameStatsContextForAgent(
  teamId: string,
  sport: Sport,
  cardsWithPlayers: (PlayerCard & { player?: { id: string; first_name: string; last_name: string; number?: number } })[]
): Promise<string | null> {
  if (sport !== 'Baseball' && sport !== 'Softball') return null;

  try {
    const { data: rows, error } = await supabase
      .from('sport_stats')
      .select('player_card_id, stat_key, stat_value')
      .eq('team_id', teamId)
      .not('game_id', 'is', null);

    if (error || !rows?.length) return null;

    const byCard = new Map<string, Array<{ stat_key: string; stat_value: number }>>();
    for (const row of rows) {
      const key = row.player_card_id;
      if (!byCard.has(key)) byCard.set(key, []);
      byCard.get(key)!.push({ stat_key: row.stat_key, stat_value: Number(row.stat_value) });
    }

    const cardToPlayer = new Map<string, { name: string; number?: number }>();
    for (const c of cardsWithPlayers) {
      if (c.player)
        cardToPlayer.set(c.id, {
          name: `${c.player.first_name} ${c.player.last_name}`.trim(),
          number: c.player.number,
        });
    }

    const lines: string[] = [];
    for (const [playerCardId, rawStats] of byCard) {
      const player = cardToPlayer.get(playerCardId);
      if (!player) continue;
      const calculated = getCalculatedStats(sport, rawStats);
      const raw = rawStats.reduce((acc, s) => ({ ...acc, [s.stat_key]: (acc[s.stat_key] || 0) + s.stat_value }), {} as Record<string, number>);

      const parts: string[] = [];
      // Batting
      if (calculated.AVG != null) parts.push(`AVG .${calculated.AVG.toFixed(3).replace(/^0\./, '')}`);
      if (calculated.OBP != null) parts.push(`OBP .${calculated.OBP.toFixed(3).replace(/^0\./, '')}`);
      if (calculated.SLG != null) parts.push(`SLG .${calculated.SLG.toFixed(3).replace(/^0\./, '')}`);
      if (calculated.OPS != null) parts.push(`OPS .${calculated.OPS.toFixed(3).replace(/^0\./, '')}`);
      if (raw['AB'] != null) parts.push(`AB ${raw['AB']}`);
      if (raw['H'] != null) parts.push(`H ${raw['H']}`);
      if (raw['R'] != null) parts.push(`R ${raw['R']}`);
      if (raw['RBI'] != null) parts.push(`RBI ${raw['RBI']}`);

      // Pitching (when present)
      const hasPitching = raw['IP'] != null || raw['K'] != null || raw['BF'] != null || raw['H_Allowed'] != null || raw['BB_Allowed'] != null || raw['R_Allowed'] != null || raw['HBP_Allowed'] != null || raw['WP'] != null || raw['Balk'] != null;
      if (hasPitching) {
        if (raw['IP'] != null) parts.push(`IP ${raw['IP']}`);
        if (raw['BF'] != null) parts.push(`BF ${raw['BF']}`);
        if (raw['K'] != null) parts.push(`K ${raw['K']}`);
        if (raw['BB_Allowed'] != null) parts.push(`BB_Allowed ${raw['BB_Allowed']}`);
        if (raw['H_Allowed'] != null) parts.push(`H_Allowed ${raw['H_Allowed']}`);
        if (raw['R_Allowed'] != null) parts.push(`R_Allowed ${raw['R_Allowed']}`);
        if (raw['ER'] != null) parts.push(`ER ${raw['ER']}`);
        if (raw['HBP_Allowed'] != null) parts.push(`HBP_Allowed ${raw['HBP_Allowed']}`);
        if (raw['WP'] != null) parts.push(`WP ${raw['WP']}`);
        if (raw['Balk'] != null) parts.push(`Balk ${raw['Balk']}`);
        if (calculated.ERA != null) parts.push(`ERA ${calculated.ERA.toFixed(2)}`);
        if (calculated.WHIP != null) parts.push(`WHIP ${calculated.WHIP.toFixed(2)}`);
        if (raw['Pitches'] != null) parts.push(`Pitches ${raw['Pitches']}`);
      }

      const num = player.number != null ? ` #${player.number}` : '';
      lines.push(`${player.name}${num}: ${parts.join(' | ')}`);
    }

    if (lines.length === 0) return null;
    return [
      '--- GAME PERFORMANCE (batting + pitching from recorded games; use to optimize lineups and pitching decisions) ---',
      ...lines,
      '---',
    ].join('\n');
  } catch {
    return null;
  }
}
